/**
 * Koroshi · drawer de navegación v2
 * Pestañas con línea deslizante + arrastre (táctil y ratón), grupos plegables (L2),
 * categorías con subcategorías en columna (L3 › L4). La apertura/cierre del drawer
 * la gestiona header-drawer.js (details/summary); aquí solo la navegación interna.
 */

const SWIPE_MIN = 40; // px horizontales para cambiar de pestaña
const TAB_KEY = 'kd:tab';

function init(root) {
  if (root.dataset.kdReady === '1') return;
  root.dataset.kdReady = '1';

  const tablist = root.querySelector('[data-kd-tablist]');
  const tabs = Array.from(root.querySelectorAll('[data-kd-tab]'));
  const panels = Array.from(root.querySelectorAll('[data-kd-panel]'));
  const line = root.querySelector('[data-kd-line]');
  const scroller = root.querySelector('[data-kd-panels]');
  if (!tabs.length || !panels.length) return;

  let current = 0;
  let animating = false;

  /* ---------- pestañas ---------- */
  function placeLine(tab, immediate) {
    if (!line || !tab) return;
    const x = tab.offsetLeft;
    const w = tab.offsetWidth;
    if (immediate) line.style.transition = 'none';
    line.style.setProperty('--x', x + 'px');
    line.style.width = w + 'px';
    if (immediate) {
      void line.offsetWidth; // fuerza reflow
      line.style.transition = '';
    }
  }

  function setTab(index, { immediate = false } = {}) {
    index = Math.max(0, Math.min(tabs.length - 1, index));
    if (index === current && !immediate) return;
    const prevPanel = panels[current];
    const nextPanel = panels[index];

    tabs.forEach((t, i) => {
      t.classList.toggle('is-active', i === index);
      t.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
    placeLine(tabs[index], immediate);

    // el contenido cambia con fundido; la línea se desliza
    const swap = () => {
      prevPanel.hidden = true;
      prevPanel.classList.remove('is-fading');
      nextPanel.hidden = false;
      nextPanel.classList.add('is-fading');
      void nextPanel.offsetWidth;
      nextPanel.classList.remove('is-fading');
      if (scroller) scroller.scrollTop = 0;
      relayoutOpenColumns(nextPanel);
      animating = false;
    };

    if (immediate || prevPanel === nextPanel) {
      swap();
    } else {
      animating = true;
      prevPanel.classList.add('is-fading');
      setTimeout(swap, 160);
    }

    current = index;
    try { sessionStorage.setItem(TAB_KEY, String(index)); } catch (e) { /* sin storage */ }
  }

  tabs.forEach((tab, i) => {
    tab.addEventListener('click', () => setTab(i));
    tab.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); tabs[(i + 1) % tabs.length].focus(); setTab((i + 1) % tabs.length); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); const j = (i - 1 + tabs.length) % tabs.length; tabs[j].focus(); setTab(j); }
    });
  });

  /* ---------- arrastre lateral (táctil + ratón) ---------- */
  if (scroller) {
    let startX = 0, startY = 0, tracking = false, dragged = false, pointerId = null;

    scroller.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      tracking = true; dragged = false; pointerId = e.pointerId;
      startX = e.clientX; startY = e.clientY;
    });

    scroller.addEventListener('pointermove', (e) => {
      if (!tracking || e.pointerId !== pointerId) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (!dragged && Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        dragged = true;
      }
    });

    const end = (e) => {
      if (!tracking || e.pointerId !== pointerId) return;
      tracking = false;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (dragged && Math.abs(dx) >= SWIPE_MIN && Math.abs(dx) > Math.abs(dy) && !animating) {
        setTab(dx < 0 ? current + 1 : current - 1);
        // evita que el clic posterior (ratón) active un enlace
        scroller.dataset.kdSuppressClick = '1';
        setTimeout(() => { delete scroller.dataset.kdSuppressClick; }, 350);
      }
      dragged = false;
    };
    scroller.addEventListener('pointerup', end);
    scroller.addEventListener('pointercancel', end);
    scroller.addEventListener('click', (e) => {
      if (scroller.dataset.kdSuppressClick === '1') { e.preventDefault(); e.stopPropagation(); }
    }, true);
    // sin selección de texto al arrastrar con ratón
    scroller.addEventListener('dragstart', (e) => e.preventDefault());
  }

  /* ---------- grupos (L2): arriba→abajo + fade ---------- */
  root.querySelectorAll('[data-kd-group-toggle]').forEach((btn) => {
    const group = btn.closest('[data-kd-group]');
    const wrap = group.querySelector('.kdrawer__l3wrap');
    const list = group.querySelector('.kdrawer__l3list');
    if (group.dataset.open === 'true' && list) list.classList.add('is-open-visible');

    btn.addEventListener('click', () => {
      const open = group.dataset.open === 'true';
      if (open) {
        // cerrar: antes de plegar, cerramos cualquier L4 abierto dentro y recortamos overflow
        group.querySelectorAll('[data-kd-l3].is-open').forEach((li) => closeL3(li, true));
        list && list.classList.remove('is-open-visible');
        group.dataset.open = 'false';
        btn.setAttribute('aria-expanded', 'false');
      } else {
        group.dataset.open = 'true';
        btn.setAttribute('aria-expanded', 'true');
      }
    });

    if (wrap && list) {
      wrap.addEventListener('transitionend', (e) => {
        if (e.propertyName !== 'grid-template-rows') return;
        if (group.dataset.open === 'true') list.classList.add('is-open-visible');
        relayoutOpenColumns(group.closest('[data-kd-panel]'));
      });
    }
  });

  /* ---------- categorías (L3) con subcategorías (L4) ---------- */
  function positionL4(li) {
    const col = li.querySelector('[data-kd-l4]');
    const panel = li.closest('[data-kd-panel]');
    if (!col || !panel) return;
    const cs = getComputedStyle(root);
    const baseX = parseFloat(cs.getPropertyValue('--kd-l4x')) || 0;
    const gap = parseFloat(cs.getPropertyValue('--kd-l4-gap')) || 16;
    const rowH = parseFloat(cs.getPropertyValue('--kd-row')) || 32;
    const panelLeft = panel.getBoundingClientRect().left;
    const parentBtn = li.querySelector('[data-kd-l3-toggle]');
    const parentRight = parentBtn ? parentBtn.getBoundingClientRect().right - panelLeft : 0;

    // La columna arranca en la fila del padre, en la X configurada.
    // Solo se desplaza a la derecha si el propio padre (texto + chevron) la invade.
    void rowH;
    col.style.top = li.offsetTop + 'px';
    const left = Math.max(baseX, Math.round(parentRight + gap));
    col.style.left = left + 'px';
    col.style.width = 'calc(100% - ' + left + 'px - var(--kd-pad))';
  }

  function closeL3(li, instant) {
    const col = li.querySelector('[data-kd-l4]');
    const btn = li.querySelector('[data-kd-l3-toggle]');
    if (instant && col) { col.style.transition = 'none'; }
    li.classList.remove('is-open');
    btn && btn.setAttribute('aria-expanded', 'false');
    if (instant && col) { void col.offsetWidth; col.style.transition = ''; }
  }

  function openL3(li) {
    const panel = li.closest('[data-kd-panel]');
    // abrir otro cierra el anterior de golpe
    panel.querySelectorAll('[data-kd-l3].is-open').forEach((other) => { if (other !== li) closeL3(other, true); });
    positionL4(li);
    li.classList.add('is-open');
    const btn = li.querySelector('[data-kd-l3-toggle]');
    btn && btn.setAttribute('aria-expanded', 'true');
  }

  function relayoutOpenColumns(panel) {
    if (!panel) return;
    panel.querySelectorAll('[data-kd-l3].is-open').forEach(positionL4);
  }

  root.querySelectorAll('[data-kd-l3-toggle]').forEach((btn) => {
    const li = btn.closest('[data-kd-l3]');
    btn.addEventListener('click', () => {
      if (li.classList.contains('is-open')) closeL3(li, false);
      else openL3(li);
    });
  });

  /* ---------- estado inicial y reajustes ---------- */
  // 1) la página tiene género (producto, colección o página) → esa pestaña, y se recuerda;
  // 2) si no, la última pestaña usada en la sesión; 3) si no, la primera (MUJER).
  let saved = 0;
  try { saved = parseInt(sessionStorage.getItem(TAB_KEY) || '0', 10) || 0; } catch (e) { saved = 0; }
  const ctxTab = root.dataset.kdGeneroTab;
  if (ctxTab !== undefined && ctxTab !== '') {
    saved = parseInt(ctxTab, 10) || 0;
  }
  setTab(saved, { immediate: true });

  const details = root.closest('details');
  if (details) {
    details.addEventListener('toggle', () => {
      if (details.open) {
        // al abrir el drawer las medidas ya son reales: recoloca línea y columnas
        requestAnimationFrame(() => { placeLine(tabs[current], true); relayoutOpenColumns(panels[current]); });
      } else {
        // al cerrar el drawer, cerrar columnas L4 abiertas (sin animación)
        root.querySelectorAll('[data-kd-l3].is-open').forEach((li) => closeL3(li, true));
      }
    });
  }

  let raf = 0;
  window.addEventListener('resize', () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => { placeLine(tabs[current], true); relayoutOpenColumns(panels[current]); });
  });
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => placeLine(tabs[current], true));
  }
}

function boot() {
  document.querySelectorAll('[data-kdrawer]').forEach(init);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
document.addEventListener('shopify:section:load', boot);
